import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { RankmiTemplateConfiguration } from '@rankmi/rankmi-templates'
import { Button, Spin } from 'antd'
import { jsPDF } from 'jspdf'
import Cookies from 'js-cookie'
import { useNotification } from 'hooks/notifications/useNotification'
import { useDc3PublishNotificationFromRoute } from 'hooks/useDc3PublishNotificationFromRoute'
import { API } from 'config'
import { certificatesService } from 'api/administration/certificates'
import { INewCertificate } from 'interfaces/certificate'
import GeneratePdf from 'components/reusables/generatePdf'
import Title from './components/title'
import Courses from './components/courses'
import '@rankmi/rankmi-templates/dist/index.css'
import { Wrapper, Preview } from './styled'
import AssociationOfCourses from './components/associationOfCourses'
import BackgroundImage from './components/backgroundImage'
import CreatedAt from './components/createdAt'
import { TableSkeleton } from 'components/reusables/skeletons'
import { MobileEvents } from 'enum/mobileEvents.enum'

const PREVIEW_FOOTER_SLOT_ID = 'lms-certificate-template-preview-slot'

const TEMPLATE_PATH_MAIN = 'main'

interface IProps {
  inMainView: boolean
  onSecondaryViewChange?: (isSecondary: boolean) => void
}

const Templates = ({ inMainView, onSecondaryViewChange }: IProps) => {
  const { t } = useTranslation()
  const translatePrefix = 'administration.certificate.list.create'
  const notification = useNotification()
  useDc3PublishNotificationFromRoute()
  const [certificateId, setCertificateId] = useState(0)
  const [content, setContent] = useState('')
  const [bgImage, setBgImage] = useState<File | string | null>(null)
  const bgImageRef = useRef(bgImage)
  bgImageRef.current = bgImage
  const [showPreviewButton, setShowPreviewButton] = useState(false)
  const [previewFooterSlot, setPreviewFooterSlot] =
    useState<HTMLElement | null>(null)
  const [generatePreview, setGeneratePreview] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const syncEditableFooter = () => {
      const editable = document.querySelector('#card .editable')
      setShowPreviewButton(!!editable)

      if (!editable) {
        setPreviewFooterSlot(null)
        return
      }

      const footerRow = editable.querySelector(
        ':scope > .ant-tabs + div > .ant-row'
      ) as HTMLElement | null

      if (!footerRow) {
        setPreviewFooterSlot(null)
        return
      }

      let slot = footerRow.querySelector(
        `#${PREVIEW_FOOTER_SLOT_ID}`
      ) as HTMLElement | null

      if (!slot) {
        slot = document.createElement('div')
        slot.id = PREVIEW_FOOTER_SLOT_ID
        footerRow.insertBefore(slot, footerRow.firstChild)
      }

      setPreviewFooterSlot((current) => (current === slot ? current : slot))
    }

    const observer = new MutationObserver(syncEditableFooter)
    observer.observe(document.body, { childList: true, subtree: true })
    syncEditableFooter()

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (inMainView) setBgImage(null)
  }, [inMainView])

  useEffect(() => {
    if (certificateId > 0) {
      onSecondaryViewChange?.(true)
    }
  }, [certificateId, onSecondaryViewChange])

  /* rankmi-templates renderiza " Nueva plantilla" con espacio inicial; ajuste visual sin tocar el paquete */
  useEffect(() => {
    if (!inMainView) return

    const normalizeCreateButtonLabel = () => {
      const card = document.getElementById('card')
      const button = card?.querySelector<HTMLButtonElement>(
        '._i1RyO .ant-btn-primary'
      )
      if (!button) return

      button.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE && node.textContent) {
          const trimmed = node.textContent.trim()
          if (node.textContent !== trimmed) {
            node.textContent = trimmed
          }
        }
      })
    }

    normalizeCreateButtonLabel()

    const card = document.getElementById('card')
    const observer = new MutationObserver(normalizeCreateButtonLabel)
    observer.observe(card ?? document.body, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [inMainView])

  const fullOverwriteColumns = (
    _actionsItems: (data: any) => Array<{ title: string; onClick: () => void }>,
    _permissions: any
  ) => [
    {
      key: 'name',
      title: t(`${translatePrefix}.table.headers.name`),
      visible: true,
      sorteable: true,
      render: (_: string, data: INewCertificate) => (
        <Title value={data.name} certificateType={data.certificate_type} />
      ),
    },
    {
      key: 'created_at',
      title: t(`${translatePrefix}.table.headers.createAt`),
      visible: true,
      sorteable: true,
      render: (_: number, data: INewCertificate) => (
        <CreatedAt value={data.created_at} active={data.active} />
      ),
    },
    {
      key: 'certificate_course_attributes.length',
      title: t(`${translatePrefix}.table.headers.courses`),
      visible: true,
      sorteable: true,
      render: (_: number, data: INewCertificate) => (
        <Courses
          certificateId={data.id}
          data={data.certificate_course_attributes.map(
            (item: { name: string }) => item.name
          )}
        />
      ),
    },
  ]

  const nodes = {
    newTemplateHeaderNode: (
      <BackgroundImage image={bgImage} setImage={setBgImage} />
    ),
    editTemplateHeaderNode: (
      <BackgroundImage image={bgImage} setImage={setBgImage} />
    ),
  }

  const createTemplate = (
    baseProps: { resource: number },
    newTemplate: { title: string; content: string; workspace: string }
  ) => {
    return saveTemplate(
      newTemplate.title,
      newTemplate.content,
      newTemplate.workspace,
      baseProps.resource
    )
  }

  const onEditTemplate = (data: { id: number }) => {
    certificatesService.getConfiguration(data.id).then((response) => {
      setBgImage(response.data.data.attributes.backgroundImage || null)
    })
  }

  const updateTemplate = (
    baseProps: { resource: number },
    templateToken: string,
    newTemplate: { title: string; content: string; workspace: string }
  ) => {
    return saveTemplate(
      newTemplate.title,
      newTemplate.content,
      newTemplate.workspace,
      baseProps.resource,
      templateToken
    )
  }

  const saveTemplate = async (
    title: string,
    content: string,
    workspace: string,
    resource: number,
    templateToken?: string
  ) => {
    if (title.trim().length > 0 && content) {
      await certificatesService
        .saveTemplate(
          title,
          content,
          bgImageRef.current,
          workspace,
          resource,
          templateToken
        )
        .then((response) => {
          setCertificateId(response.data.data.attributes.certificateId)
          if (templateToken) {
            notification.success(
              t(`${translatePrefix}.notifications.updatedCertificate`)
            )
          } else {
            notification.success(
              t(`${translatePrefix}.notifications.newCertificateHasBeenCreated`)
            )
          }
        })
        .catch(() => {
          if (templateToken) {
            notification.warning(
              t(`${translatePrefix}.notifications.failedToUpdate`)
            )
          } else {
            notification.warning(
              t(`${translatePrefix}.notifications.failedToCreate`)
            )
          }
        })
      return { sucess: true }
    } else {
      notification.warning(
        t(`${translatePrefix}.notifications.fillInTheFields`)
      )
      return { sucess: true }
    }
  }

  const actionsCallback = useMemo(
    () => ({
      createTemplate,
      onEditTemplate,
      updateTemplate,
      onPathChange: (path: string) => {
        onSecondaryViewChange?.(path !== TEMPLATE_PATH_MAIN)
      },
    }),
    [onSecondaryViewChange]
  )

  const onGeneratePreview = () => {
    const iframe = document.querySelector(
      '.tox-edit-area__iframe'
    ) as HTMLIFrameElement
    if (iframe && iframe.contentDocument) {
      const element = iframe.contentDocument.getElementById('tinymce')
      if (element) {
        setGenerating(true)
        certificatesService
          .getPreviewContent(element.innerHTML)
          .then((response) => {
            setContent(response.data)
            setGeneratePreview(true)
          })
          .catch(() => {
            setGenerating(false)
          })
      }
    }
  }

  const sendEventToMobileWebView = useCallback(
    (eventType: string, data: any) => {
      if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: eventType,
            data,
          })
        )
      }
    },
    []
  )

  const onGenerate = (pdf: jsPDF | null) => {
    if (pdf) {
      const isAppMobile = Cookies.getJSON('rankmi_flavour_mobile')

      if (isAppMobile) {
        const arrayBuffer = pdf.output('arraybuffer')
        sendEventToMobileWebView(MobileEvents.CERTIFICATE_PREVIEW_PDF, {
          buffer: Array.from(new Uint8Array(arrayBuffer)),
          fileName: `certificate-preview.pdf`,
          mimeType: 'application/pdf',
        })
      } else {
        const blob = pdf.output('blob')
        const url = URL.createObjectURL(blob)
        window.open(url)
      }
    }
    setGeneratePreview(false)
    setGenerating(false)
  }

  const onDuplicate = async (token: string) => {
    setLoading(true)
    await certificatesService.duplicate(token).catch(() => setLoading(false))
    setLoading(false)
  }

  return certificateId ? (
    <AssociationOfCourses
      certificateId={certificateId}
      setCertificateId={setCertificateId}
    />
  ) : (
    <Wrapper>
      {loading ? (
        <TableSkeleton />
      ) : (
        <RankmiTemplateConfiguration
          context="lms"
          baseUrl={`//${API}`}
          resource={1}
          url={`//${API}/administration/certificates?q=%7B%22search_name_cont%22:%22%22%7D`}
          nodes={nodes}
          actionsCallback={actionsCallback}
          onDuplicate={onDuplicate}
          fullOverwriteColumns={fullOverwriteColumns}
        />
      )}
      {previewFooterSlot &&
        createPortal(
          <Preview>
            <Spin spinning={generating}>
              <Button onClick={onGeneratePreview}>
                {t(`${translatePrefix}.preview`)}
              </Button>
            </Spin>
          </Preview>,
          previewFooterSlot
        )}
      {showPreviewButton && generatePreview && (
        <GeneratePdf
          content={content}
          backgroundImage={bgImage}
          onGenerate={onGenerate}
          preview={true}
        />
      )}
    </Wrapper>
  )
}

export default Templates
